const STORAGE_KEY = 'recentlyViewedProducts';
// 検索APIのデフォルトページサイズ（paginateタグ未使用時）が10件のため、保存上限も10件とする。
// 表示時に現在閲覧中の商品を除外するため、実際の表示は最大9件になる。
const MAX_ITEMS = 10;
const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.currentProductId = this.dataset.currentProductId
      ? Number(this.dataset.currentProductId)
      : null;
  }

  connectedCallback() {
    if (this.currentProductId) {
      this._saveCurrentProduct();
    }
    this._fetchProducts();
  }

  // --- localStorage management ---

  _getItems() {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const now = Date.now();
      return items.filter((entry) => now - entry.timestamp < EXPIRY_MS);
    } catch {
      return [];
    }
  }

  _saveItems(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch {
      // Safari 10.x以前のプライベートブラウジングモードでは localStorage.setItem() が
      // QuotaExceededError を投げる。Safari 11（2017年）で修正済みだが、防御的に残す。
      // ref: https://bugs.webkit.org/show_bug.cgi?id=157010
    }
  }

  _saveCurrentProduct() {
    const items = this._getItems();
    const existingIndex = items.findIndex((entry) => entry.id === this.currentProductId);

    if (existingIndex !== -1) {
      items.splice(existingIndex, 1);
    }

    items.unshift({ id: this.currentProductId, timestamp: Date.now() });
    this._saveItems(items);
  }

  // --- Fetch and render ---

  _fetchProducts() {
    const items = this._getItems();
    const ids = items
      .map((entry) => entry.id)
      .filter((id) => id !== this.currentProductId);

    if (ids.length === 0) return;

    const queryString = ids.map((id) => 'id:' + id).join(' OR ');
    const searchUrl = window.routes.search_url || '/search';
    const url = `${searchUrl}?view=recently-viewed-products&type=product&q=${encodeURIComponent(queryString)}`;

    fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const sourceContainer = tempDiv.querySelector('[data-recently-viewed-products]');
        if (!sourceContainer) return;

        const productItems = Array.from(sourceContainer.querySelectorAll('[data-product-id]'));
        if (productItems.length === 0) return;

        // Reorder DOM elements to match localStorage order
        const ordered = [];
        for (const id of ids) {
          const el = productItems.find((item) => Number(item.dataset.productId) === id);
          if (el) ordered.push(el);
        }

        if (ordered.length === 0) return;

        const placeholder = this.querySelector('.recently-viewed-products__grid');
        if (!placeholder) return;

        // Assign slider IDs and classes to each item
        const sliderId = placeholder.id;
        placeholder.innerHTML = '';
        ordered.forEach((el, index) => {
          el.id = `Slide-${sliderId.replace('Slider-', '')}-${index + 1}`;
          el.classList.add('slider__slide');
          placeholder.appendChild(el);
        });

        // Show the section
        this.closest('.shopify-section').style.display = 'block';

        // Re-initialize slider
        const slider = this.querySelector('slider-component');
        if (slider && typeof slider.resetPages === 'function') {
          slider.resetPages();
        }

        // When there is only 1 item, SliderComponent.initPages() returns early
        // (length < 2) without calling update(), so buttons are never disabled.
        // Explicitly disable them so the CSS :has() rule hides the container.
        if (ordered.length < 2) {
          const prevBtn = this.querySelector('.slider-button--prev');
          const nextBtn = this.querySelector('.slider-button--next');
          if (prevBtn) prevBtn.setAttribute('disabled', 'disabled');
          if (nextBtn) nextBtn.setAttribute('disabled', 'disabled');
        }
      })
      .catch((error) => {
        console.error('Recently viewed products: failed to fetch', error);
      });
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
